var _class, _descriptor, _token, _identifier, _relatedTokenMap;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { dependentKeyCompat } from '@ember/object/compat';
import { DEBUG } from '@glimmer/env';
import { tracked } from '@glimmer/tracking';
import { cached } from "ember-cached-decorator-polyfill";
import { resolve } from 'rsvp';
import { unsubscribe } from '../record-notification-manager';
import { internalModelFactoryFor, recordIdentifierFor } from '../store/internal-model-factory';
import Reference from './reference';
/**
  @module @ember-data/store
*/

/**
 A `HasManyReference` is a low-level API that allows users and addon
 authors to perform meta-operations on a has-many relationship.

 @class HasManyReference
 @public
 @extends Reference
 */
let HasManyReference = (_class = (_token = /*#__PURE__*/_classPrivateFieldLooseKey("token"), _identifier = /*#__PURE__*/_classPrivateFieldLooseKey("identifier"), _relatedTokenMap = /*#__PURE__*/_classPrivateFieldLooseKey("relatedTokenMap"), class HasManyReference extends Reference {
  constructor(store, parentIdentifier, hasManyRelationship, key) {
    super(store, parentIdentifier);
    // unsubscribe tokens given to us by the notification manager
    Object.defineProperty(this, _token, {
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, _identifier, {
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, _relatedTokenMap, {
      writable: true,
      value: void 0
    });
    _initializerDefineProperty(this, "_ref", _descriptor, this);
    this.key = key;
    this.hasManyRelationship = hasManyRelationship;
    this.type = hasManyRelationship.definition.type;
    this.parent = internalModelFactoryFor(store).peek(parentIdentifier).recordReference;
    _classPrivateFieldLooseBase(this, _identifier)[_identifier] = parentIdentifier;
    _classPrivateFieldLooseBase(this, _token)[_token] = store._notificationManager.subscribe(parentIdentifier, (_, bucket, notifiedKey) => {
      if ((bucket === 'relationships' || bucket === 'property') && notifiedKey === key) {
        this._ref++;
      }
    });
    _classPrivateFieldLooseBase(this, _relatedTokenMap)[_relatedTokenMap] = new Map();
    // TODO inverse
  }
  destroy() {
    unsubscribe(_classPrivateFieldLooseBase(this, _token)[_token]);
    _classPrivateFieldLooseBase(this, _relatedTokenMap)[_relatedTokenMap].forEach(token => {
      unsubscribe(token);
    });
    _classPrivateFieldLooseBase(this, _relatedTokenMap)[_relatedTokenMap].clear();
  }
  get _relatedIdentifiers() {
    this._ref; // consume the tracked prop

    let resource = this._resource();
    _classPrivateFieldLooseBase(this, _relatedTokenMap)[_relatedTokenMap].forEach(token => {
      unsubscribe(token);
    });
    _classPrivateFieldLooseBase(this, _relatedTokenMap)[_relatedTokenMap].clear();
    if (resource && resource.data) {
      return resource.data.map(resourceIdentifier => {
        const identifier = this.store.identifierCache.getOrCreateRecordIdentifier(resourceIdentifier);
        const token = this.store._notificationManager.subscribe(identifier, (_, bucket, notifiedKey) => {
          if (bucket === 'identity' || (bucket === 'attributes' || bucket === 'property') && notifiedKey === 'id') {
            this._ref++;
          }
        });
        _classPrivateFieldLooseBase(this, _relatedTokenMap)[_relatedTokenMap].set(identifier, token);
        return identifier;
      });
    }
    return [];
  }
  _resource() {
    return this.recordData.getHasMany(this.key);
  }

  /**
   This returns a string that represents how the reference will be
   looked up when it is loaded. If the relationship has a link it will
   use the "link" otherwise it defaults to "id".
    Example
    ```app/models/post.js
   import Model, { hasMany } from '@ember-data/model';
    export default class PostModel extends Model {
     @hasMany({ async: true }) comments;
   }
   ```
    ```javascript
   let post = store.push({
     data: {
       type: 'post',
       id: 1,
       relationships: {
         comments: {
           data: [{ type: 'comment', id: 1 }]
         }
       }
     }
   });
    let commentsRef = post.hasMany('comments');
    // get the identifier of the reference
   if (commentsRef.remoteType() === "ids") {
     let ids = commentsRef.ids();
   } else if (commentsRef.remoteType() === "link") {
     let link = commentsRef.link();
   }
   ```
    @method remoteType
   @public
   @return {String} The name of the remote type. This should either be `link` or `ids`
   */
  remoteType() {
    let value = this._resource();
    if (value && value.links && value.links.related) {
      return 'link';
    }
    return 'ids';
  }

  /**
   `ids()` returns an array of the record IDs in this relationship.
    Example
    ```app/models/post.js
   import Model, { hasMany } from '@ember-data/model';
    export default class PostModel extends Model {
     @hasMany({ async: true }) comments;
   }
   ```
    ```javascript
   let post = store.push({
     data: {
       type: 'post',
       id: 1,
       relationships: {
         comments: {
           data: [{ type: 'comment', id: 1 }]
         }
       }
     }
   });
    let commentsRef = post.hasMany('comments');
    commentsRef.ids(); // ['1']
   ```
    @method ids
    @public
   @return {Array} The ids in this has-many relationship
   */
  ids() {
    return this._relatedIdentifiers.map(identifier => identifier.id);
  }

  /**
   `push` can be used to update the data in the relationship and Ember
   Data will treat the new data as the canonical value of this
   relationship on the backend.
    Example
    ```app/models/post.js
   import Model, { hasMany } from '@ember-data/model';
    export default class PostModel extends Model {
     @hasMany({ async: true }) comments;
   }
   ```
    ```
   let post = store.push({
     data: {
       type: 'post',
       id: 1,
       relationships: {
         comments: {
           data: [{ type: 'comment', id: 1 }]
         }
       }
     }
   });
    let commentsRef = post.hasMany('comments');
    commentsRef.ids(); // ['1']
    commentsRef.push([
   [{ type: 'comment', id: 2 }],
   [{ type: 'comment', id: 3 }],
   ])
    commentsRef.ids(); // ['2', '3']
   ```
    @method push
    @public
   @param {Array|Promise} objectOrPromise a promise that resolves to a JSONAPI document object describing the new value of this relationship.
   @return {ManyArray}
   */
  async push(objectOrPromise) {
    const payload = await resolve(objectOrPromise);
    let array;
    if (!Array.isArray(payload) && typeof payload === 'object' && Array.isArray(payload.data)) {
      array = payload.data;
    } else {
      array = payload;
    }
    const internalModel = internalModelFactoryFor(this.store).peek(_classPrivateFieldLooseBase(this, _identifier)[_identifier]);
    const {
      store
    } = this;
    let identifiers = array.map(obj => {
      let record;
      if ('data' in obj) {
        // TODO deprecate pushing non-valid JSON:API here
        record = store.push(obj);
      } else {
        record = store.push({
          data: obj
        });
      }
      if (DEBUG) {
        let relationshipMeta = this.hasManyRelationship.definition;
        let identifier = this.hasManyRelationship.identifier;
      }
      return recordIdentifierFor(record);
    });
    const {
      graph,
      identifier
    } = this.hasManyRelationship;
    store._backburner.join(() => {
      graph.push({
        op: 'replaceRelatedRecords',
        record: identifier,
        field: this.key,
        value: identifiers
      });
    });

    // TODO IGOR it seems wrong that we were returning the many array here
    return internalModel.getHasMany(this.key);
  }
  _isLoaded() {
    let hasRelationshipDataProperty = this.hasManyRelationship.state.hasReceivedData;
    if (!hasRelationshipDataProperty) {
      return false;
    }
    let members = this.hasManyRelationship.currentState;

    //TODO Igor cleanup
    return members.every(identifier => {
      let internalModel = this.store._internalModelForResource(identifier);
      return internalModel.currentState.isLoaded === true;
    });
  }

  /**
   `value()` synchronously returns the current value of the has-many
   relationship. Unlike `record.get('relationshipName')`, calling
   `value()` on a reference does not trigger a fetch if the async
   relationship is not yet loaded. If the relationship is not loaded
   it will always return `null`.
    Example
    ```app/models/post.js
   import Model, { hasMany } from '@ember-data/model';
    export default class PostModel extends Model {
     @hasMany({ async: true }) comments;
   }
   ```
    ```javascript
   let post = store.push({
     data: {
       type: 'post',
       id: 1,
       relationships: {
         comments: {
           data: [{ type: 'comment', id: 1 }]
         }
       }
     }
   });
    let commentsRef = post.hasMany('comments');
    post.get('comments').then(function(comments) {
     commentsRef.value() === comments
   })
   ```
    @method value
    @public
   @return {ManyArray}
   */
  value() {
    const internalModel = internalModelFactoryFor(this.store).peek(_classPrivateFieldLooseBase(this, _identifier)[_identifier]);
    if (this._isLoaded()) {
      return internalModel.getManyArray(this.key);
    }
    return null;
  }

  /**
   Loads the relationship if it is not already loaded.  If the
   relationship is already loaded this method does not trigger a new
   load. This causes a request to the specified
   relationship link or reloads all items currently in the relationship.
    Example
    ```app/models/post.js
   import Model, { hasMany } from '@ember-data/model';
    export default class PostModel extends Model {
     @hasMany({ async: true }) comments;
   }
   ```
    ```javascript
   let post = store.push({
     data: {
       type: 'post',
       id: 1,
       relationships: {
         comments: {
           data: [{ type: 'comment', id: 1 }]
         }
       }
     }
   });
    let commentsRef = post.hasMany('comments');
    commentsRef.load().then(function(comments) {
     //...
   });
   ```
    You may also pass in an options object whose properties will be
   fed forward. This enables you to pass `adapterOptions` into the
   request given to the adapter via the reference.
    Example
    ```javascript
   commentsRef.load({ adapterOptions: { isPrivate: true } })
     .then(function(comments) {
       //...
     });
   ```
    ```app/adapters/comment.js
   export default ApplicationAdapter.extend({
     findMany(store, type, id, snapshots) {
       // In the adapter you will have access to adapterOptions.
       let adapterOptions = snapshots[0].adapterOptions;
     }
   });
   ```
    @method load
    @public
   @param {Object} options the options to pass in.
   @return {Promise} a promise that resolves with the ManyArray in
   this has-many relationship.
   */
  load(options) {
    const internalModel = internalModelFactoryFor(this.store).peek(_classPrivateFieldLooseBase(this, _identifier)[_identifier]);
    return internalModel.getHasMany(this.key, options);
  }

  /**
   Reloads this has-many relationship. This causes a request to the specified
   relationship link or reloads all items currently in the relationship.
    Example
    ```app/models/post.js
   import Model, { hasMany } from '@ember-data/model';
    export default class PostModel extends Model {
     @hasMany({ async: true }) comments;
   }
   ```
    ```javascript
   let post = store.push({
     data: {
       type: 'post',
       id: 1,
       relationships: {
         comments: {
           data: [{ type: 'comment', id: 1 }]
         }
       }
     }
   });
    let commentsRef = post.hasMany('comments');
    commentsRef.reload().then(function(comments) {
     //...
   });
   ```
    You may also pass in an options object whose properties will be
   fed forward. This enables you to pass `adapterOptions` into the
   request given to the adapter via the reference. A full example
   can be found in the `load` method.
    Example
    ```javascript
   commentsRef.reload({ adapterOptions: { isPrivate: true } })
   ```
    @method reload
    @public
   @param {Object} options the options to pass in.
   @return {Promise} a promise that resolves with the ManyArray in this has-many relationship.
   */
  reload(options) {
    const internalModel = internalModelFactoryFor(this.store).peek(_classPrivateFieldLooseBase(this, _identifier)[_identifier]);
    return internalModel.reloadHasMany(this.key, options);
  }
}), _descriptor = _applyDecoratedDescriptor(_class.prototype, "_ref", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _applyDecoratedDescriptor(_class.prototype, "_relatedIdentifiers", [cached, dependentKeyCompat], Object.getOwnPropertyDescriptor(_class.prototype, "_relatedIdentifiers"), _class.prototype), _class);
export { HasManyReference as default };